/**
 * @fileoverview Shared dirty-navigation guard for template editor flows.
 * @module ui-admin/hooks/useTemplateDirtyGuard
 *
 * Responsibilities:
 * - Register a browser beforeunload prompt while local-first edits are unsaved.
 * - Provide a host-callable confirmation helper for route/button navigation.
 *
 * Dependencies:
 * - React effect/memo hooks.
 *
 * Architecture:
 * - Local-first guard only; it does not persist data or call APIs.
 * - Host apps decide when a template is dirty based on Dexie/save state.
 *
 * Notes:
 * - React Router blockers are host-specific; this hook exposes a route-agnostic confirmation helper.
 */
import { useCallback, useEffect } from 'react';

/**
 * @contract UseTemplateDirtyGuardOptions
 *
 * Purpose:
 * - Configures dirty navigation protection for template editor pages and slideouts.
 *
 * Fields:
 * - isDirty: Whether unsaved local changes exist.
 * - enabled: Optional switch to disable the guard.
 * - message: Browser/confirm prompt text.
 *
 * Rules:
 * - `isDirty` must come from local editor state, not direct API request state.
 *
 * Sync:
 * - Does not sync; protects local-first edits until host save logic completes.
 */
export interface UseTemplateDirtyGuardOptions {
  isDirty: boolean;
  enabled?: boolean;
  message?: string;
}

/**
 * @contract UseTemplateDirtyGuardResult
 *
 * Purpose:
 * - Return shape for template dirty guard consumers.
 *
 * Fields:
 * - shouldBlockNavigation: True when host navigation should confirm with the user.
 * - confirmNavigation: Returns true when navigation may continue.
 *
 * Rules:
 * - Hosts must call `confirmNavigation` before custom route changes that bypass browser unload.
 *
 * Sync:
 * - Read-only state derived from host local-first save status.
 */
export interface UseTemplateDirtyGuardResult {
  shouldBlockNavigation: boolean;
  confirmNavigation: () => boolean;
}

const DEFAULT_MESSAGE = 'You have unsaved template changes. Leave without saving?';

/**
 * @hook useTemplateDirtyGuard
 *
 * Purpose:
 * - Protects template editor users from losing unsaved local-first edits during navigation.
 *
 * Returns:
 * - shouldBlockNavigation and confirmNavigation helper.
 *
 * Side Effects:
 * - Registers/removes the browser `beforeunload` event listener when dirty.
 *
 * Offline Behavior:
 * - Works entirely in-browser and does not require connectivity.
 */
export function useTemplateDirtyGuard({
  isDirty,
  enabled = true,
  message = DEFAULT_MESSAGE,
}: UseTemplateDirtyGuardOptions): UseTemplateDirtyGuardResult {
  const shouldBlockNavigation = enabled && isDirty;

  useEffect(() => {
    if (!shouldBlockNavigation) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = message;
      return message;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [message, shouldBlockNavigation]);

  const confirmNavigation = useCallback(() => {
    if (!shouldBlockNavigation) return true;
    return window.confirm(message);
  }, [message, shouldBlockNavigation]);

  return { shouldBlockNavigation, confirmNavigation };
}
